import { RenderPageProps } from '@react-pdf-viewer/core';
import { ChangeEvent, MouseEvent } from 'react';
import { RndDragCallback, RndResizeCallback } from 'react-rnd';
import { Sign } from '../../../model/sign';
import ButtonText from '../buttons/ButtonText';
import AspectRatioIcon from '../icons/AspectRatioIcon';
import ClearIcon from '../icons/ClearIcon';
import SaveIcon from '../icons/SaveIcon';
import RndArea from '../rnds/RndArea';
import InputSignItem from '../signs/InputSignItem';
import SignDustbin from '../signs/SignDustbin';

type RenderPagePropsOverrides = RenderPageProps & {
	signs: Sign[];
	onDragStop: RndDragCallback;
	onResizeStop: RndResizeCallback;
	onDeleteSign: (e: MouseEvent<HTMLButtonElement>, sign: Sign) => void;
	onChange: (e: ChangeEvent<HTMLInputElement>, sign: Sign) => void;
	onSaveSize?: (e: MouseEvent<HTMLButtonElement>, sign: Sign) => void;
	onLoadSize?: (e: MouseEvent<HTMLButtonElement>, sign: Sign) => void;
	enableResizing?: boolean;
	noSigns?: Sign[];
};

export default function SignRenderPage({
	width: pageWidth,
	height: pageHeight,
	scale,
	pageIndex,
	canvasLayer,
	annotationLayer,
	textLayer,
	signs,
	onDragStop,
	onResizeStop,
	onDeleteSign,
	onChange,
	onSaveSize,
	onLoadSize,
	enableResizing = true,
	noSigns = [],
}: RenderPagePropsOverrides) {
	return (
		<>
			<SignDustbin id={`${pageIndex}`} viewerZoom={scale} pageWidth={pageWidth} pageHeight={pageHeight}>
				{canvasLayer.children}
				{annotationLayer.children}
				{textLayer.children}
			</SignDustbin>

			{signs
				.filter(sign => sign.pageIndex === pageIndex)
				.map(sign => {
					const { id, imageUrl, width, height, x: prevX, y: prevY } = sign;
					const maxX = pageWidth - width * scale;
					const maxY = pageHeight - height * scale;
					const x = prevX > 0 ? prevX * scale : 0;
					const y = prevY > 0 ? prevY * scale : 0;

					return (
						<RndArea
							className='z-10 inline-flex outline-2 outline-dotted outline-transparent hover:outline-red-700 transition-[outline-color]'
							key={id}
							id={id}
							enableResizing={enableResizing}
							width={width * scale}
							height={height * scale}
							x={x > maxX ? maxX : x}
							y={y > maxY ? maxY : y}
							onDragStop={onDragStop}
							onResizeStop={onResizeStop}
						>
							<img className='pointer-events-none object-contain' src={imageUrl} alt='sign item' />

							<div className='absolute left-0 top-0 -translate-y-[110%] flex items-center gap-4'>
								{enableResizing && (
									<InputSignItem
										id={String(id)}
										value={Math.floor(width)}
										onChange={onChange}
										sign={sign}
									/>
								)}

								{onSaveSize && enableResizing && (
									<ButtonText
										className='flex min-w-0 p-0 hover:cursor-pointer'
										onClick={e => onSaveSize(e, sign)}
									>
										<SaveIcon fontSize='small' />
									</ButtonText>
								)}

								{onLoadSize && enableResizing && (
									<ButtonText
										className='flex min-w-0 p-0 hover:cursor-pointer'
										onClick={e => onLoadSize(e, sign)}
									>
										<AspectRatioIcon fontSize='small' />
									</ButtonText>
								)}

								<ButtonText
									className='flex min-w-0 p-0 hover:cursor-pointer'
									onClick={e => onDeleteSign(e, sign)}
								>
									<ClearIcon fontSize='small' />
								</ButtonText>
							</div>
						</RndArea>
					);
				})}

			{noSigns
				.filter(sign => sign.pageIndex === pageIndex)
				.map(sign => {
					const { id, imageUrl, width, height, x: prevX, y: prevY } = sign;
					const maxX = pageWidth - width * scale;
					const maxY = pageHeight - height * scale;
					const x = prevX > 0 ? prevX * scale : 0;
					const y = prevY > 0 ? prevY * scale : 0;

					return (
						<RndArea
							className='z-5 inline-flex'
							key={id}
							id={id}
							enableResizing={false}
							width={width * scale}
							height={height * scale}
							x={x > maxX ? maxX : x}
							y={y > maxY ? maxY : y}
							onDragStop={() => {}}
							onResizeStop={() => {}}
							disableDragging
						>
							<img className='pointer-events-none object-contain' src={imageUrl} alt='sign item' />
						</RndArea>
					);
				})}
		</>
	);
}
